<script lang="ts">
import { computed, defineComponent, h, onUnmounted, reactive, ref, unref, watch } from 'vue';

import { classNames, getScrollBarSize, hasPreventedScroll, hMergeSlot, IN_BROWSER } from '@components/utils';

import { makeThemeProps, provideTheme, useDisplay, useRender } from '../../composable';
import { CDS_DRAWER_WIDTH } from '../CdsDrawer/CdsDrawer.vue';

import { LayoutResizeObserver, LayoutScrollObserver } from './components';
import { useProvideLayout } from './context';

import type { TScrollObserver } from './components';
import type { ILayoutContextProps } from './context';
import type { PropType, VNode } from 'vue';

export const componentName = 'CdsLayout';
export default defineComponent({
  name: componentName,
  props: {
    container: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    view: {
      type: String as PropType<string>,
      default: 'hhh lpr fff',
      validator: (value: string) => /^(h|l)h(h|r) lpr (f|l)f(f|r)$/.test(value.toLowerCase())
    },
    ...makeThemeProps()
  },
  emits: {
    resize: (contentRect: Pick<DOMRectReadOnly, 'width' | 'height'>) => true,
    scroll: (info: TScrollObserver) => true,
    scrollHeight: (height: number) => true
  },
  setup(props, { slots, emit, expose }) {
    const display = useDisplay();
    const { themeClasses } = provideTheme(props);

    const rootRef = ref<HTMLElement | undefined>();

    const height = display.height;
    const width = props.container ? ref(0) : display.width;
    const scroll = ref<TScrollObserver>({
      position: { top: 0, left: 0 },
      direction: 'down',
      directionChanged: false,
      delta: { top: 0, left: 0 },
      inflectionPoint: { top: 0, left: 0 }
    });

    const containerHeight = ref(0);
    const scrollbarWidth = ref(getScrollBarSize());

    const classes = computed(() => 'cds-layout cds-layout--' + (props.container ? 'containerized' : 'standard'));
    const style = computed(() => (!props.container ? { minHeight: unref(display.screenHeight) + 'px' } : null));
    const targetStyle = computed(() =>
      unref(scrollbarWidth) !== 0 ? { ['right']: `${unref(scrollbarWidth)}px` } : null
    );
    const targetChildStyle = computed(() =>
      scrollbarWidth.value !== 0
        ? {
            ['left']: 0,
            ['right']: `-${unref(scrollbarWidth)}px`,
            width: `calc(100% + ${unref(scrollbarWidth)}px)`
          }
        : null
    );

    function onPageScroll(data: TScrollObserver) {
      if (props.container || !hasPreventedScroll()) {
        scroll.value = {
          position: data.position,
          direction: data.direction,
          directionChanged: data.directionChanged,
          inflectionPoint: data.inflectionPoint,
          delta: data.delta
        };

        emit('scroll', unref(scroll));
      }
    }

    function onPageResize(contentRect: Pick<DOMRectReadOnly, 'width' | 'height'>) {
      const { width: newWidth, height: newHeight } = contentRect;

      let resized = false;

      if (unref(height) !== newHeight) {
        resized = true;
        height.value = newHeight;

        props.onScrollHeight !== undefined && emit('scrollHeight', newHeight);
        updateScrollbarWidth();
      }

      if (unref(width) !== newWidth) {
        resized = true;
        width.value = newWidth;
      }

      if (resized) emit('resize', contentRect);
    }

    function onContainerResize({ height }: Pick<DOMRectReadOnly, 'width' | 'height'>) {
      if (unref(containerHeight) !== height) {
        containerHeight.value = height;
        updateScrollbarWidth();
      }
    }

    function updateScrollbarWidth() {
      if (props.container) {
        const width = height.value > containerHeight.value ? getScrollBarSize() : 0;

        if (scrollbarWidth.value !== width) {
          scrollbarWidth.value = width;
        }
      }
    }

    let animateTimer: any = null;

    const layout: ILayoutContextProps = {
      instances: {},
      view: computed(() => props.view),
      isContainer: computed(() => props.container),
      rootRef,
      height,
      containerHeight,
      scrollbarWidth,
      totalWidth: computed(() => unref(width) + unref(scrollbarWidth)),
      rows: computed(() => {
        const rows = props.view.toLowerCase().split(' ');

        return {
          top: rows[0].split(''),
          middle: rows[1].split(''),
          bottom: rows[2].split('')
        };
      }),
      header: reactive({ size: 0, offset: 0, space: false }),
      footer: reactive({ size: 0, offset: 0, space: false }),
      right: reactive({ size: CDS_DRAWER_WIDTH, offset: 0, space: false }),
      left: reactive({ size: CDS_DRAWER_WIDTH, offset: 0, space: false }),
      scroll,
      animate() {
        if (animateTimer !== null) {
          clearTimeout(animateTimer);
        } else {
          document.body.classList.add('cds-body--layout-animate');
        }

        animateTimer = setTimeout(() => {
          animateTimer = null;
          document.body.classList.remove('cds-body--layout-animate');
        }, 155);
      },
      update(part, prop, value) {
        layout[part][prop] = value;
      }
    };
    useProvideLayout(layout);

    // prevent scrollbar flicker while resizing window height
    // if no page scrollbar is already present
    if (IN_BROWSER && getScrollBarSize() > 0) {
      let timer: any = null;
      const el = document.body;

      const restoreScrollbar = () => {
        timer = null;
        el.classList.remove('cds-hide-scrollbar');
      };

      const hideScrollbar = () => {
        if (timer === null) {
          if (el.scrollHeight > unref(display.height)) return;
          el.classList.add('cds-hide-scrollbar');
        } else {
          clearTimeout(timer);
        }

        timer = setTimeout(restoreScrollbar, 300);
      };

      const updateScrollEvent = (action: 'add' | 'remove') => {
        if (timer !== null && action === 'remove') {
          clearTimeout(timer);
          restoreScrollbar();
        }

        window[`${action}EventListener`]('resize', hideScrollbar);
      };

      watch(() => (!props.container ? 'add' : 'remove'), updateScrollEvent);

      !props.container && updateScrollEvent('add');

      onUnmounted(() => {
        updateScrollEvent('remove');
      });
    }

    useRender(() => {
      const content = hMergeSlot(slots.default, [
        h(LayoutScrollObserver, { onScroll: onPageScroll }),
        h(LayoutResizeObserver, { onResize: onPageResize })
      ]);

      const layout: VNode = h(
        'div',
        {
          ref: props.container ? undefined : rootRef,
          class: classNames(unref(classes), unref(themeClasses)),
          style: unref(style),
          tabindex: -1
        },
        content
      );

      if (props.container) {
        return h('div', { class: 'cds-layout__container', ref: rootRef }, [
          h(LayoutResizeObserver, { onResize: onContainerResize }),
          h(
            'div',
            {
              class: 'cds-layout-container__target',
              style: unref(targetStyle)
            },
            [h('div', { class: 'cds-scrollable', style: unref(targetChildStyle) }, [layout])]
          )
        ]);
      }

      return layout;
    });

    expose({
      ...layout
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsLayout';
</style>
